import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Banner from '../components/Banner';
import Chip from '../components/Chip';
import { CveText } from '../components/CveLink';
import EmptyState from '../components/EmptyState';
import Icon from '../components/Icon';
import PageHeader from '../components/PageHeader';
import { PageSpinner } from '../components/Spinner';
import { errorMessage, fetchCmdbGraph } from '../lib/api';
import type { GraphResponse } from '../lib/types';
import { EntityIcon, NodeState, ViewIcon } from './beziehungen/ExplorerShared';
import { classLabel, customerSummary, initialRoot, matchesNode } from './beziehungen/explorerModel';
import { buildModel } from './beziehungen/graphModel';
import { FocusGraph } from './beziehungen/FocusGraph';
import { OrbitView } from './beziehungen/OrbitView';
import RelationView from './beziehungen/RelationView';
import './beziehungen/beziehungen.css';

type View = 'relations' | 'graph' | 'orbit';
const VIEWS: { id: View; label: string }[] = [
  { id: 'relations', label: 'Datenblatt' },
  { id: 'graph', label: 'Fokus-Graph' },
  { id: 'orbit', label: 'Orbit' },
];

export default function BeziehungenPage(): ReactNode {
  const [data, setData] = useState<GraphResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<View>('relations');
  const [rootId, setRootId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [klass, setKlass] = useState('');
  const [limit, setLimit] = useState(8);
  const request = useRef(0);
  const pickerRef = useRef<HTMLDivElement>(null);
  const pickerButtonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (): Promise<void> => {
    const sequence = ++request.current;
    setLoading(true);
    try {
      const response = await fetchCmdbGraph();
      if (sequence !== request.current) return;
      setData(response); setLoadError(null);
    } catch (error) {
      if (sequence === request.current) setLoadError(errorMessage(error));
    } finally {
      if (sequence === request.current) setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); return () => { ++request.current; }; }, [load]);

  const model = useMemo(() => data ? buildModel(data) : null, [data]);
  const resolvedRoot = model ? (rootId && model.byId.has(rootId) ? rootId : initialRoot(model)) : null;
  const root = resolvedRoot ? model?.byId.get(resolvedRoot) : null;
  const selected = (selectedId ? model?.byId.get(selectedId) : null) ?? root;
  const summary = useMemo(() => (model && root ? customerSummary(model, root.id) : null), [model, root]);
  const plurals = useMemo(() => Object.fromEntries((data?.classes ?? []).map((entry) => [entry.name, entry.plural])), [data]);
  const matches = useMemo(() => (model?.nodes ?? []).filter((node) => (!klass || node.klass === klass) && matchesNode(node, query)), [model, klass, query]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    searchRef.current?.focus();
    const close = (event: PointerEvent): void => {
      if (event.target instanceof Node && !pickerRef.current?.contains(event.target)) setPickerOpen(false);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [pickerOpen]);

  function chooseRoot(id: string): void {
    setRootId(id); setSelectedId(id); setPickerOpen(false); setQuery(''); setLimit(8);
  }

  if (loading && !data) return <PageSpinner text="Lädt Beziehungen …" />;

  return <div className="explorer-page">
    <PageHeader title="Beziehungen" />
    {loadError ? <Banner kind="error">{loadError}</Banner> : null}
    {data?.unavailable.length ? <Banner kind="warn">Einige Daten sind momentan nicht verfügbar: {data.unavailable.map(classLabel).join(', ')}. Die Ansichten zeigen die verfügbaren Beziehungen.</Banner> : null}

    <div className="tabs explorer-view-switch" role="tablist" aria-label="Darstellung der Beziehungen">
      {VIEWS.map((item, index) => <button type="button" role="tab" id={`explorer-tab-${item.id}`} key={item.id} aria-selected={view === item.id} aria-controls={`explorer-panel-${item.id}`} tabIndex={view === item.id ? 0 : -1} onClick={() => setView(item.id)} onKeyDown={(event) => {
        let next = index;
        if (event.key === 'ArrowRight') next = (index + 1) % VIEWS.length;
        else if (event.key === 'ArrowLeft') next = (index + VIEWS.length - 1) % VIEWS.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = VIEWS.length - 1;
        else return;
        event.preventDefault(); const target = VIEWS[next];
        if (target) { setView(target.id); document.getElementById(`explorer-tab-${target.id}`)?.focus(); }
      }}>
        <span className="explorer-view-icon"><ViewIcon name={item.id} /></span>{item.label}
      </button>)}
    </div>

    {model && root ? <section className="explorer-workspace" aria-label="Beziehungs-Explorer" aria-busy={loading}>
      <div className="explorer-context">
        <div className="explorer-root-picker" ref={pickerRef} onKeyDown={(event) => { if (event.key === 'Escape' && pickerOpen) { event.stopPropagation(); setPickerOpen(false); pickerButtonRef.current?.focus(); } }}>
          <button type="button" className="explorer-root-button" ref={pickerButtonRef} aria-haspopup="dialog" aria-expanded={pickerOpen} aria-controls={pickerOpen ? 'explorer-picker' : undefined} aria-label={`Ausgangspunkt: ${root.label} (${classLabel(root.klass)}). Anderen Ausgangspunkt wählen`} title="Anderen Ausgangspunkt wählen" onClick={() => setPickerOpen((open) => !open)}>
            <EntityIcon klass={root.klass} /><span><small>{classLabel(root.klass)}</small><strong>{root.label}</strong></span><Icon name="chevron-down" className="explorer-root-chevron" />
          </button>
          {pickerOpen ? <div className="explorer-picker-popover" id="explorer-picker">
            <div className="explorer-picker-head"><strong>Ausgangspunkt wählen</strong><button type="button" className="close-x" aria-label="Auswahl schließen" onClick={() => { setPickerOpen(false); pickerButtonRef.current?.focus(); }}><Icon name="close" /></button></div>
            <label className="explorer-search"><Icon name="search" /><input ref={searchRef} type="search" aria-label="Ausgangspunkt suchen" placeholder="Kunde, Produkt, Komponente, CVE …" value={query} onChange={(event) => { setQuery(event.target.value); setLimit(8); }} /></label>
            <div className="explorer-picker-filter"><select aria-label="Datensatzart" value={klass} onChange={(event) => { setKlass(event.target.value); setLimit(8); }}><option value="">Alle Datensatzarten</option>{data?.classes.filter((entry) => entry.count > 0).map((entry) => <option key={entry.name} value={entry.name}>{entry.plural}</option>)}</select><span role="status">{matches.length} Treffer</span></div>
            <div className="explorer-picker-results">{matches.slice(0, limit).map((node) => <button type="button" key={node.id} className={node.id === root.id ? 'is-current' : ''} onClick={() => { chooseRoot(node.id); pickerButtonRef.current?.focus(); }}><EntityIcon klass={node.klass} /><span><strong>{node.label}</strong><small>{node.context ? `${classLabel(node.klass)} · ${node.context}` : classLabel(node.klass)}</small></span><Icon name="arrow-right" /></button>)}</div>
            {matches.length === 0 ? <EmptyState compact title="Keine Datensätze gefunden">Ändern Sie den Suchbegriff oder die Datensatzart.</EmptyState> : null}
            {matches.length > limit ? <button type="button" className="explorer-picker-more" onClick={() => setLimit((current) => current + 16)}>Weitere Datensätze anzeigen ({matches.length - limit})</button> : null}
          </div> : null}
        </div>
        {/* Customer at a glance: what its instances add up to. */}
        {summary && summary.instances > 0 ? <div className="explorer-summary" aria-label="Zusammenfassung">
          <Chip>{summary.instances} {summary.instances === 1 ? 'Produktinstanz' : 'Produktinstanzen'}</Chip>
          {summary.kevAffected > 0
            ? <Chip tone="alert" title="Instanzen, deren aktuelle Konfiguration eine KEV-betroffene Komponentenversion enthält">{summary.kevAffected} mit KEV-Treffer</Chip>
            : <Chip tone="good">Keine KEV-Treffer</Chip>}
          {summary.notificationsOpen > 0
            ? <Chip tone="warn">{summary.notificationsOpen} {summary.notificationsOpen === 1 ? 'Benachrichtigung' : 'Benachrichtigungen'} offen</Chip>
            : summary.notificationsSent > 0 ? <Chip tone="good">{summary.notificationsSent} benachrichtigt</Chip> : null}
          {summary.customVersions > 0 ? <Chip title="Produktinstanzen, die auf einer eigenen Kundenversion laufen">{summary.customVersions} {summary.customVersions === 1 ? 'Kundenversion' : 'Kundenversionen'}</Chip> : null}
        </div> : null}
      </div>
      <div className="explorer-views" key={root.id}>
        <div id="explorer-panel-relations" role="tabpanel" aria-labelledby="explorer-tab-relations" hidden={view !== 'relations'}><RelationView model={model} rootId={root.id} plurals={plurals} onSelect={setSelectedId} onRootChange={chooseRoot} /></div>
        <div id="explorer-panel-graph" role="tabpanel" aria-labelledby="explorer-tab-graph" hidden={view !== 'graph'}><FocusGraph model={model} rootId={root.id} selectedId={selected?.id ?? null} onSelect={setSelectedId} /></div>
        <div id="explorer-panel-orbit" role="tabpanel" aria-labelledby="explorer-tab-orbit" hidden={view !== 'orbit'}><OrbitView model={model} rootId={root.id} selectedId={selected?.id ?? null} onSelect={setSelectedId} onRootChange={chooseRoot} /></div>
      </div>
      {/* The record sheet carries its own header; the graph views need this bar. */}
      {selected && view !== 'relations' ? <div className="explorer-detail" aria-label="Ausgewählter Datensatz">
        <EntityIcon klass={selected.klass} /><div className="explorer-detail-copy"><span className="explorer-kicker">{classLabel(selected.klass)} · #{selected.cardId}</span><strong><CveText text={selected.label} /></strong>{selected.detail ? <span><CveText text={selected.detail} /></span> : null}</div>
        <NodeState state={selected.state} /><div className="explorer-detail-actions">{selected.id !== root.id ? <button type="button" onClick={() => chooseRoot(selected.id)}><ViewIcon name="orbit" />Als Ausgangspunkt</button> : null}<Link to={`/stammdaten?klasse=${encodeURIComponent(selected.klass)}&id=${selected.cardId}`}>In Stammdaten öffnen <Icon name="external" size={12} /></Link></div>
      </div> : null}
    </section> : <EmptyState
      title={loadError ? 'Beziehungen konnten nicht geladen werden' : 'Noch keine Datensätze'}
      action={loadError ? <button type="button" onClick={() => void load()}>Erneut versuchen</button> : <Link to="/stammdaten">Zu den Stammdaten <Icon name="external" size={12} /></Link>}
    >
      {loadError ? 'Versuchen Sie, die Daten erneut zu laden.' : 'Legen Sie Kunden, Produkte oder Komponenten in den Stammdaten an.'}
    </EmptyState>}
  </div>;
}
